import { useState, useEffect, useCallback } from 'react';
import conversationService from '../services/conversationService';

/**
 * Custom hook for managing conversation cache status and optimization
 * 
 * @param {string} conversationId - The conversation ID
 * @returns {Object} Cache hook results
 */
const useConversationCache = (conversationId) => {
  const [cacheStatus, setCacheStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch cache status
  const fetchCacheStatus = useCallback(async () => {
    if (!conversationId) return;

    setLoading(true);
    setError(null);

    try {
      const status = await conversationService.getCacheStatus(conversationId);
      setCacheStatus(status);
    } catch (err) {
      console.error('Error fetching cache status:', err);
      setError(err.message);
      // Set default status on error
      setCacheStatus({
        conversation_id: conversationId,
        is_cached: false,
        cache_created_at: null,
        cache_expires_at: null,
        messages_cached: 0,
        total_cache_creation_tokens: 0,
        total_cache_read_tokens: 0,
        time_until_expiry: null
      });
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  // Auto-fetch cache status when conversationId changes
  useEffect(() => {
    fetchCacheStatus();
  }, [fetchCacheStatus]);

  // Calculate cache efficiency metrics
  const cacheEfficiency = cacheStatus ? {
    totalTokensSaved: cacheStatus.total_cache_read_tokens,
    cacheHitRatio: cacheStatus.total_cache_read_tokens > 0 ? 
      (cacheStatus.total_cache_read_tokens / (cacheStatus.total_cache_creation_tokens + cacheStatus.total_cache_read_tokens)) : 0,
    isActiveCache: cacheStatus.is_cached && cacheStatus.time_until_expiry > 0,
    timeUntilExpiry: cacheStatus.time_until_expiry,
    expiryDate: cacheStatus.cache_expires_at ? new Date(cacheStatus.cache_expires_at) : null
  } : null;

  // Format cache status for display
  const formatCacheStatus = useCallback(() => {
    if (!cacheStatus) return null;

    const formatTime = (seconds) => {
      if (!seconds || seconds <= 0) return 'Expired';
      
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    };

    const formatTokens = (tokens) => {
      if (!tokens || tokens === 0) return '0';
      if (tokens >= 1000) {
        return `${(tokens / 1000).toFixed(1)}K`;
      }
      return tokens.toString();
    };

    return {
      status: cacheStatus.is_cached ? 'cached' : 'not_cached',
      timeRemaining: formatTime(cacheStatus.time_until_expiry),
      tokensSaved: formatTokens(cacheStatus.total_cache_read_tokens),
      tokensCreated: formatTokens(cacheStatus.total_cache_creation_tokens),
      messagesCount: cacheStatus.messages_cached || 0,
      efficiency: cacheEfficiency?.cacheHitRatio ? 
        `${(cacheEfficiency.cacheHitRatio * 100).toFixed(1)}%` : '0%'
    };
  }, [cacheStatus, cacheEfficiency]);

  // Check if cache should be refreshed
  const shouldRefreshCache = useCallback(() => {
    if (!cacheStatus) return true;
    
    // Refresh if cache is about to expire (less than 5 minutes)
    if (cacheStatus.time_until_expiry && cacheStatus.time_until_expiry < 300) {
      return true;
    }
    
    // Refresh if no cache exists
    if (!cacheStatus.is_cached) {
      return true;
    }
    
    return false;
  }, [cacheStatus]);

  // Get cache optimization recommendations
  const getCacheRecommendations = useCallback(() => {
    if (!cacheStatus) return [];

    const recommendations = [];

    if (!cacheStatus.is_cached) {
      recommendations.push({
        type: 'info',
        message: 'Cache will be created automatically on your next interaction',
        action: null
      });
    } else if (cacheStatus.time_until_expiry < 300) {
      recommendations.push({
        type: 'warning',
        message: 'Cache expires soon. Next interaction will refresh it.',
        action: 'continue_conversation'
      });
    } else if (cacheStatus.total_cache_read_tokens === 0) {
      recommendations.push({
        type: 'tip',
        message: 'Continue the conversation to benefit from cached context',
        action: 'continue_conversation'
      });
    } else {
      recommendations.push({
        type: 'success',
        message: `Cache is active and has saved ${formatCacheStatus()?.tokensSaved || '0'} tokens`,
        action: null
      });
    }

    return recommendations;
  }, [cacheStatus, formatCacheStatus]);

  return {
    cacheStatus,
    cacheEfficiency,
    loading,
    error,
    fetchCacheStatus,
    formatCacheStatus,
    shouldRefreshCache,
    getCacheRecommendations,
    // Convenience properties
    isCached: cacheStatus?.is_cached || false,
    isExpiringSoon: cacheStatus?.time_until_expiry < 300,
    tokensSaved: cacheStatus?.total_cache_read_tokens || 0,
    tokensCreated: cacheStatus?.total_cache_creation_tokens || 0
  };
};

export default useConversationCache;